import { DAY_MS, MINUTE_MS, SECOND_MS } from './timings';

/**
 * How long ago, in the words the screen it is on needs. Here rather than beside
 * the component that renders a timestamp: these are text, both apps read them,
 * and `packages/ui` ships source with no build to test against. `Timestamp`
 * stays there, because it is markup.
 */

/**
 * A moment, as near as it needs to be: "Just now", a clock, or a date. Three
 * screens had written this out, two with the day and the minute as raw
 * arithmetic. Locale-dependent unlike `Timestamp`: prose, not a log field.
 */
export function whenNear(iso: string): string {
  const at = new Date(iso);
  const age = Date.now() - at.getTime();
  if (age < MINUTE_MS) return 'Just now';
  if (age < DAY_MS) return at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return at.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * How long ago, short: `42s ago`, `18m ago`, `3h ago`, `2d ago`. Distinct from
 * `whenNear`, which answers "when": this answers "how long has this been going
 * on", the only question an operations screen asks -- "started at 14:02" tells
 * you nothing without doing the arithmetic yourself.
 */
export function agoShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / SECOND_MS);
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${Math.max(0, seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  const days = Math.round(seconds / 86_400);
  if (days < 61) return `${days}d ago`;
  // Past two months a day count stops meaning anything: "584d ago" is read
  // twice and divided in the head, where "1y 7m ago" is read once.
  const months = Math.round(days / 30.44);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest === 0 ? `${years}y ago` : `${years}y ${rest}mo ago`;
}

/** A span of seconds as a person says it: `48s`, `1m 18s`. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 60) return `${whole}s`;
  return `${Math.floor(whole / 60)}m ${whole % 60}s`;
}

/** How long since a moment, in the same words as {@link duration}. */
export function elapsed(iso: string): string {
  return duration((Date.now() - new Date(iso).getTime()) / SECOND_MS);
}
